import {
  Bell,
  ClipboardList,
  FileWarning,
  Megaphone,
  Receipt,
  Timer,
  type LucideIcon,
} from 'lucide-react';

import type { Notificacao } from '@/modulos/notificacoes/contrato';

/**
 * Aparência de um aviso de notificação — o que a lista da tela e a gaveta do
 * cabeçalho compartilham.
 *
 * A referência aprovada desenha cada aviso com **um ícone próprio do tipo**
 * dentro de um avatar redondo e, no canto desse avatar, **um ponto colorido
 * pela prioridade** — sempre visível, inclusive no aviso já lido
 * (`src/lib/notificacaoHelpers.ts:53-60` na referência: tom crítico para alta,
 * tom de atenção para média, tom neutro para baixa e para o aviso sem
 * prioridade). A marca de "não lida" é **outra coisa**, e aparece em três
 * lugares ao mesmo tempo: o fundo do item, o título em negrito e um **segundo
 * ponto**, sem cor de prioridade, na lateral direita do item — presente só
 * quando o aviso não foi lido (`NotificacoesPopover.tsx:123-136` e
 * `NotificacoesList.tsx:75-88` na referência). Dois pontos, dois papéis: o do
 * avatar informa prioridade; o lateral informa leitura.
 *
 * O registro está em `docs/rastreabilidade/matriz-paridade-analises.md`.
 */
const ICONE_POR_TIPO: Record<string, LucideIcon> = {
  PARAMETRO_VIGENCIA: Megaphone,
  COBRANCA_GERADA: Receipt,
  MULTA_APLICADA: Receipt,
  PENDENCIA_DOCUMENTO: FileWarning,
  DOCUMENTACAO_PENDENTE: FileWarning,
  NOVA_SOLICITACAO: ClipboardList,
  SOLICITACAO_EM_ANALISE: ClipboardList,
  SLA_APONTAMENTO_VENCIDO: Timer,
};

/**
 * Ícone do aviso, pelo código do tipo que o banco grava. Tipo sem ícone
 * próprio cai no sino, como na referência.
 */
export function iconeDoAviso(item: Notificacao): LucideIcon {
  return ICONE_POR_TIPO[item.tipo] ?? Bell;
}

export const ROTULO_PRIORIDADE: Record<string, string> = {
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

/**
 * Cor do ponto de prioridade do avatar, sempre por token semântico (regra 2 de
 * interface) — equivalente aos tons que `getPrioridadeColor` usa na
 * referência: `bg-destructive` (alta), `bg-warning` (média),
 * `bg-muted-foreground` (baixa).
 */
export const CLASSE_PRIORIDADE_PONTO: Record<'alta' | 'media' | 'baixa', string> = {
  alta: 'bg-destructive',
  media: 'bg-warning',
  baixa: 'bg-muted-foreground',
};

/**
 * Cor do ponto de prioridade de um aviso. Sem prioridade cadastrada, cai no
 * mesmo tom neutro que a referência dá à baixa — o ponto nunca some, mas
 * também nunca inventa uma prioridade que o dado não tem.
 */
export function classePontoPrioridade(item: Pick<Notificacao, 'prioridade'>): string {
  return CLASSE_PRIORIDADE_PONTO[item.prioridade ?? 'baixa'];
}

/**
 * Forma do selo de tipo do aviso: pílula pequena sobre fundo neutro, como a
 * referência a desenha. Não é usada para prioridade — a prioridade virou
 * ponto no avatar, não texto (ver comentário de topo deste arquivo).
 */
export const CLASSE_SELO = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

/** Data absoluta como a referência a escreve no pé do aviso. */
export function dataAbsoluta(iso: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) {
    return '';
  }
  return quando.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Agrupador de recência, como a referência o mostra: um rótulo em caixa alta
 * acima do bloco. Na tela dela apareceu só "ANTERIORES", porque nenhum aviso
 * era do dia; o grupo "HOJE" existe e recebe os do dia corrente.
 */
export function agruparPorRecencia(
  itens: readonly Notificacao[],
): readonly { rotulo: string; itens: readonly Notificacao[] }[] {
  const hoje = new Date();
  const mesmoDia = (iso: string) => {
    const quando = new Date(iso);
    return (
      !Number.isNaN(quando.getTime()) &&
      quando.getFullYear() === hoje.getFullYear() &&
      quando.getMonth() === hoje.getMonth() &&
      quando.getDate() === hoje.getDate()
    );
  };
  const doDia = itens.filter((item) => mesmoDia(item.criadaEm));
  const anteriores = itens.filter((item) => !mesmoDia(item.criadaEm));

  return [
    { rotulo: 'Hoje', itens: doDia },
    { rotulo: 'Anteriores', itens: anteriores },
  ].filter((grupo) => grupo.itens.length > 0);
}

/**
 * A central de notificações da área em que a pessoa está — o destino do
 * "Ver todas as notificações" do sino, que a casca monta em todas as áreas.
 */
export function centralDaArea(caminho: string): string {
  if (caminho === '/credenciado' || caminho.startsWith('/credenciado/')) {
    return '/credenciado/notificacoes';
  }
  if (caminho === '/sme' || caminho.startsWith('/sme/')) return '/sme/notificacoes';
  return '/operacao/notificacoes';
}
